import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw,
  Copy,
  Share2,
  BookOpen,
  Heart,
  Sparkles,
} from "lucide-react";
import AppLayout from "../components/Layout/AppLayout";
import duasData from "../data/duas.json";
import ayatData from "../data/ayat.json";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import SiteNav from "../components/Layout/SiteNav";

export default function DuasAndAyat() {
  const navigate = useNavigate();
  const [currentDua, setCurrentDua] = useState(null);
  const [currentAyat, setCurrentAyat] = useState(null);
  const [fadeIn, setFadeIn] = useState(true);

  // Initialize with random items
  useEffect(() => {
    setCurrentDua(getRandomDua());
    setCurrentAyat(getRandomAyat());
  }, []);

  const getRandomDua = () => {
    const randomIndex = Math.floor(Math.random() * duasData.length);
    return duasData[randomIndex];
  };

  const getRandomAyat = () => {
    const randomIndex = Math.floor(Math.random() * ayatData.length);
    return ayatData[randomIndex];
  };

  const handleRefreshDua = () => {
    setFadeIn(false);
    setTimeout(() => {
      setCurrentDua(getRandomDua());
      setFadeIn(true);
    }, 300);
  };

  const handleRefreshAyat = () => {
    setFadeIn(false);
    setTimeout(() => {
      setCurrentAyat(getRandomAyat());
      setFadeIn(true);
    }, 300);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    // Optional: Add a toast notification here if you have toast setup
  };

  const handleShare = async (item, type) => {
    const text =
      type === "dua"
        ? `${item.translation}\n\n${item.arabic}\n\nSource: ${item.virtue}`
        : `${item.english}\n\n${item.arabic}\n\nReference: ${item.reference}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: type === "dua" ? "Dua" : "Quran Verse",
          text: text,
        });
      } catch (err) {
        console.error("Error sharing:", err);
      }
    } else {
      handleCopy(text);
    }
  };

  // Helper to normalize Arabic text for better wrapping
  const normalizeArabicText = (text) => {
    if (!text) return "";
    // Keep the text as-is but ensure proper spacing
    return text.replace(/\s+/g, " ").trim();
  };

  const content = (
    <div className="mx-auto max-w-4xl space-y-8 py-8">
      <div className="space-y-4 text-center">
        <div className="flex items-center justify-center gap-3">
          <BookOpen className="h-10 w-10 text-primary" />
          <h1 className="text-4xl font-bold tracking-tight">
            Daily Duas & Ayat
          </h1>
        </div>
        <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
          Strengthen your connection with Allah through daily supplications and
          Quranic verses. Each dua and ayat is a source of guidance and
          blessings.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Badge variant="secondary" className="text-sm">
            <Heart className="mr-1 h-3 w-3" />
            {duasData.length} Duas
          </Badge>
          <Badge variant="secondary" className="text-sm">
            <Sparkles className="mr-1 h-3 w-3" />
            {ayatData.length} Ayat
          </Badge>
        </div>
      </div>

      <Tabs defaultValue="duas" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="duas">Duas (Supplications)</TabsTrigger>
          <TabsTrigger value="ayat">Ayat (Quranic Verses)</TabsTrigger>
        </TabsList>

        {/* Duas Tab */}
        <TabsContent value="duas">
          {currentDua && (
            <Card
              className={cn(
                "border-2 shadow-lg transition-all duration-300",
                fadeIn ? "opacity-100 scale-100" : "opacity-0 scale-95"
              )}
            >
              <CardHeader className="space-y-4 pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-2xl flex items-center gap-2">
                    <Heart className="h-6 w-6 text-primary" />
                    Today's Dua
                  </CardTitle>
                  <Button
                    onClick={handleRefreshDua}
                    size="sm"
                    variant="outline"
                    className="gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    New Dua
                  </Button>
                </div>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Arabic Text */}
                <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-8 overflow-hidden">
                  <div
                    dir="rtl"
                    className="text-3xl md:text-4xl"
                    style={{
                      fontFamily: "'Noto Naskh Arabic', serif",
                      lineHeight: "2.5",
                      textAlign: "right",
                      width: "100%",
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "normal",
                      maxWidth: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    {normalizeArabicText(currentDua.arabic)}
                  </div>
                </div>

                <Separator />

                {/* English Translation */}
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    Translation
                  </h3>
                  <p className="text-xl leading-relaxed text-foreground">
                    {currentDua.translation}
                  </p>
                </div>

                {/* Virtue/Source */}
                {currentDua.virtue && (
                  <div className="rounded-xl bg-muted/50 p-6 space-y-2 border">
                    <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-primary">
                      <BookOpen className="h-4 w-4" />
                      Virtue & Source
                    </div>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {currentDua.virtue}
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap justify-center gap-3 pt-4">
                  <Button
                    onClick={() =>
                      handleCopy(
                        `${currentDua.translation}\n\n${currentDua.arabic}${
                          currentDua.virtue
                            ? `\n\nSource: ${currentDua.virtue}`
                            : ""
                        }`
                      )
                    }
                    variant="outline"
                    className="gap-2"
                  >
                    <Copy className="h-4 w-4" />
                    Copy
                  </Button>
                  <Button
                    onClick={() => handleShare(currentDua, "dua")}
                    variant="outline"
                    className="gap-2"
                  >
                    <Share2 className="h-4 w-4" />
                    Share
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Ayat Tab */}
        <TabsContent value="ayat">
          {currentAyat && (
            <Card
              className={cn(
                "border-2 shadow-lg transition-all duration-300",
                fadeIn ? "opacity-100 scale-100" : "opacity-0 scale-95"
              )}
            >
              <CardHeader className="space-y-4 pb-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-2xl flex items-center gap-2">
                      <Sparkles className="h-6 w-6 text-primary" />
                      Today's Ayat
                    </CardTitle>
                    <CardDescription className="text-base font-medium text-primary">
                      {currentAyat.reference}
                    </CardDescription>
                  </div>
                  <Button
                    onClick={handleRefreshAyat}
                    size="sm"
                    variant="outline"
                    className="gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    New Ayat
                  </Button>
                </div>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Arabic Text */}
                <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-8 relative overflow-hidden">
                  <div className="absolute top-4 right-4 opacity-10">
                    <BookOpen className="h-16 w-16" />
                  </div>
                  <div
                    dir="rtl"
                    className="text-3xl md:text-4xl relative z-10"
                    style={{
                      fontFamily: "'Noto Naskh Arabic', serif",
                      lineHeight: "2.5",
                      textAlign: "right",
                      width: "100%",
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "normal",
                      maxWidth: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    {normalizeArabicText(currentAyat.arabic)}
                  </div>
                </div>

                <Separator />

                {/* English Translation */}
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    Translation
                  </h3>
                  <p className="text-xl leading-relaxed text-foreground">
                    {currentAyat.english}
                  </p>
                </div>

                {/* Surah Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-xl bg-muted/50 p-4 border">
                    <div className="text-sm font-semibold text-muted-foreground mb-1">
                      Surah
                    </div>
                    <div className="text-lg font-bold text-primary">
                      {currentAyat.surah}
                    </div>
                  </div>
                  <div className="rounded-xl bg-muted/50 p-4 border">
                    <div className="text-sm font-semibold text-muted-foreground mb-1">
                      Ayah Number
                    </div>
                    <div className="text-lg font-bold text-primary">
                      {currentAyat.ayah_number}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap justify-center gap-3 pt-4">
                  <Button
                    onClick={() =>
                      handleCopy(
                        `${currentAyat.english}\n\n${currentAyat.arabic}\n\nReference: ${currentAyat.reference}`
                      )
                    }
                    variant="outline"
                    className="gap-2"
                  >
                    <Copy className="h-4 w-4" />
                    Copy
                  </Button>
                  <Button
                    onClick={() => handleShare(currentAyat, "ayat")}
                    variant="outline"
                    className="gap-2"
                  >
                    <Share2 className="h-4 w-4" />
                    Share
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Info Section */}
      <Card className="bg-muted/30">
        <CardContent className="pt-6">
          <div className="space-y-3 text-center">
            <h3 className="font-semibold text-lg">About Daily Duas & Ayat</h3>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              These supplications and verses are essential spiritual
              nourishment. The duas have been taught by Prophet Muhammad ﷺ, and
              the Quranic verses are divine guidance from Allah. Make it a daily
              habit to reflect upon them.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <SiteNav />

      <div className="container mx-auto px-4">{content}</div>
    </div>
  );
}
